import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Card, DataTable, Select, Modal, FormField, TextInput, Drawer, StatusBadge, ConfirmDialog, EmptyState, Tabs } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { fmtDuration } from '@/shared/hooks/useTicker';
import { downloadCsv } from '@/shared/lib/downloadCsv';
import { LeadImportInput, type ParsedLead } from './leads/LeadImportInput';
import './leads/leads.css';
import { ScreenToolbar, FilterRow, SearchField, CampaignCell, MutedCell, timeAgo } from './leads/Toolbar';

interface Lead {
  id: number; phone: string; name?: string; status: string; attempts: number; disposition?: string;
  agent_id?: number | null; agent_name?: string | null; scheduled_callback_at?: string | null; last_attempt_at?: string | null;
  campaign_id?: number; campaign_name?: string | null;
  external_source?: string | null; external_id?: string | null;
}
interface Campaign { id: number; name: string }

/** תג קטן "מ-CRM" לליד שסונכרן מ-Kliento. */
function CrmBadge({ source, id }: { source?: string | null; id?: string | null }) {
  if (source !== 'crm') return null;
  return <span title={id ? `CRM: ${id}` : undefined}><StatusBadge tone="info" size="sm">מ-CRM</StatusBadge></span>;
}
interface AgentOpt { id: number; name: string }
interface DispOpt { code: string; label: string; color?: string }
interface LeadDetail extends Lead {
  fields: Record<string, unknown>;
  calls: { id: number; direction: string; status: string; started_at: string; talk_sec: number | null; disposition: string | null; recording_url: string | null }[];
  dispositions: DispOpt[];
}
interface LeadStats { total: number; by_status: Record<string, number>; converted: number; conversion_rate: number | null }

const STATUS_OPTS = [
  { value: '', label: 'כל הסטטוסים' },
  { value: 'queued', label: 'בהמתנה' },
  { value: 'calling', label: 'בחיוג' },
  { value: 'connected', label: 'מחובר' },
  { value: 'completed', label: 'הושלם' },
  { value: 'failed', label: 'נכשל' },
  { value: 'dnc', label: 'DNC' },
];
const STATUS_HE: Record<string, string> = Object.fromEntries(STATUS_OPTS.map((o) => [o.value, o.label]));

/**
 * switcher — בורר "לידים / אנשי קשר" של המסך המאוחד, מצויר בשורת הפעולות.
 * onTotal — מדווח את סה"כ הלידים (בלי סינון קמפיין) לספירה שבבורר.
 */
export function Leads({ switcher, onTotal }: { switcher?: ReactNode; onTotal?: (n: number) => void } = {}) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [agents, setAgents] = useState<AgentOpt[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const [agentId, setAgentId] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [parsedImport, setParsedImport] = useState<ParsedLead[]>([]);
  const [importCampaignId, setImportCampaignId] = useState('');
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  // _fields: שדות מותאמים כזוגות [שם, ערך] — עריכים בטופס, נשמרים כ-fields.
  type LeadForm = Partial<Lead> & { _fields?: [string, string][] };
  const [editing, setEditing] = useState<LeadForm>({});
  const [initialEditing, setInitialEditing] = useState<LeadForm>({});
  const [confirmState, setConfirmState] = useState<
    | { kind: 'bulkDelete'; count: number }
    | { kind: 'deleteLead'; id: number }
    | { kind: 'discardChanges' }
    | null
  >(null);

  // שינויים בטופס הליד שטרם נשמרו — סגירת ה-Drawer תבקש אישור.
  const detailDirty = detail != null && JSON.stringify(editing) !== JSON.stringify(initialEditing);

  function requestCloseDetail() {
    if (detailDirty) { setConfirmState({ kind: 'discardChanges' }); return; }
    setDetail(null);
  }

  useEffect(() => {
    // ברירת המחדל היא "המאגר" — כל הלידים מכל הקמפיינים בלי כפולים.
    void adminApi.campaigns().then((res) => setCampaigns(res.data as Campaign[]));
    void adminApi.agents({ per_page: 500 }).then((res) => setAgents((res.data as AgentOpt[]) ?? []));
  }, []);

  function loadLeads() {
    setLoading(true);
    setSelected(new Set());
    void adminApi
      .leads(activeId, { status: status || undefined, agent_id: agentId ? Number(agentId) : undefined, q: q || undefined, per_page: 200 })
      .then((res) => setRows(res.data as Lead[]))
      .finally(() => setLoading(false));
    void adminApi.leadStats(activeId).then((s) => {
      setStats(s as LeadStats);
      if (activeId == null && typeof (s as LeadStats)?.total === 'number') onTotal?.((s as LeadStats).total);
    }).catch(() => setStats(null));
  }

  useEffect(() => {
    const t = setTimeout(loadLeads, q ? 300 : 0); // debounce search
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, status, agentId, q]);

  async function doImport() {
    const targetId = activeId ?? (importCampaignId ? Number(importCampaignId) : null);
    if (targetId == null) { toast('בחר קמפיין יעד לייבוא', 'danger'); return; }
    if (!parsedImport.length) { toast('לא נמצאו לידים תקינים', 'danger'); return; }
    setImporting(true);
    try {
      const res = await adminApi.importLeads(targetId, parsedImport);
      toast(`יובאו ${res.imported} לידים${res.skipped_duplicates ? ` · ${res.skipped_duplicates} כפילויות דולגו` : ''}`, 'ok');
      setImportOpen(false);
      setParsedImport([]);
      loadLeads();
    } catch {
      toast('שגיאה בייבוא', 'danger');
    } finally {
      setImporting(false);
    }
  }

  function toggle(id: number) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  const allChecked = rows.length > 0 && selected.size === rows.length;

  function bulk(action: 'assign' | 'status' | 'delete', extra?: { agent_id?: number | null; status?: string }) {
    if (!selected.size) return;
    if (action === 'delete') { setConfirmState({ kind: 'bulkDelete', count: selected.size }); return; }
    void runBulk(action, extra);
  }

  async function runBulk(action: 'assign' | 'status' | 'delete', extra?: { agent_id?: number | null; status?: string }) {
    try {
      const n = await adminApi.bulkLeads(action, [...selected], extra);
      toast(`עודכנו ${n} לידים`, 'ok');
      loadLeads();
    } catch { toast('שגיאה בפעולה מרוכזת', 'danger'); }
  }

  async function openDetail(lead: Lead) {
    try {
      const d = (await adminApi.lead(lead.id)) as LeadDetail;
      setDetail(d);
      const initial = {
        status: d.status, disposition: d.disposition ?? '', agent_id: d.agent_id ?? null,
        name: d.name ?? '', scheduled_callback_at: d.scheduled_callback_at ?? '',
        // שדות מותאמים כזוגות [שם, ערך] — עריכים, וה-dirty-check תופס אותם
        // אוטומטית כי הם חלק מ-editing.
        _fields: Object.entries(d.fields ?? {}).map(([k, v]) => [k, String(v)] as [string, string]),
      };
      setEditing(initial);
      setInitialEditing(initial);
    } catch { toast('שגיאה בטעינת הליד', 'danger'); }
  }

  async function saveDetail() {
    if (!detail) return;
    try {
      await adminApi.updateLead(detail.id, {
        status: editing.status,
        disposition: editing.disposition || null,
        agent_id: editing.agent_id || null,
        name: editing.name || null,
        scheduled_callback_at: editing.scheduled_callback_at || null,
        fields: Object.fromEntries(((editing._fields as [string, string][]) ?? []).filter(([k]) => k.trim() !== '')),
      });
      toast('הליד עודכן', 'ok');
      setDetail(null);
      loadLeads();
    } catch { toast('שמירה נכשלה', 'danger'); }
  }

  const cols: DataTableColumn<Lead>[] = useMemo(() => [
    {
      key: '_sel', he: '', width: 36,
      heNode: (
        <input
          type="checkbox"
          checked={allChecked}
          onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
          aria-label="בחר הכל"
        />
      ),
      render: (r) => <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} aria-label="בחר" />,
    },
    { key: 'phone', he: 'טלפון', render: (r) => <PhoneCell number={r.phone} size="sm" /> },
    { key: 'name', he: 'שם', render: (r) => <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontWeight: 600 }}>{r.name || '—'}<CrmBadge source={r.external_source} id={r.external_id} /></span> },
    ...(activeId == null ? [{ key: 'campaign_name' as const, he: 'קמפיין', render: (r: Lead) => <CampaignCell id={r.campaign_id} name={r.campaign_name} /> }] : []),
    { key: 'status', he: 'סטטוס', render: (r) => <StatusBadge tone={statusTone(r.status)} size="sm">{STATUS_HE[r.status] ?? r.status}</StatusBadge> },
    { key: 'agent_name', he: 'נציג', render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.agent_name ?? '—'}</span> },
    { key: 'attempts', he: 'ניסיונות', render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.attempts}</span> },
    { key: 'disposition', he: 'תוצאה', render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.disposition || '—'}</span> },
    { key: 'last_attempt_at', he: 'מגע אחרון', render: (r) => <MutedCell>{timeAgo(r.last_attempt_at)}</MutedCell> },
  ], [selected, activeId, rows, allChecked]);

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <ScreenToolbar
        switcher={switcher}
        filters={<>
          <Select value={String(activeId ?? '')} onChange={(v) => setActiveId(v ? Number(v) : null)} options={[{ value: '', label: 'כל הקמפיינים' }, ...campaigns.map((c) => ({ value: String(c.id), label: c.name }))]} />
          <Select value={agentId} onChange={setAgentId} options={[{ value: '', label: 'כל הנציגים' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} />
        </>}
        actions={<>
          <button
            type="button"
            className="btn"
            title="ייצוא CSV"
            onClick={() =>
              downloadCsv('/admin/leads/export', {
                ...(activeId != null ? { campaign_id: activeId } : {}),
                status: status || undefined,
                agent_id: agentId ? Number(agentId) : undefined,
                q: q || undefined,
              }, 'merkaziya-leads.csv').catch(() => toast('שגיאה בייצוא', 'danger'))
            }
          >
            <Icon name="download" size={14} /> ייצוא
          </button>
          <button type="button" className="btn primary" onClick={() => { setImportCampaignId(''); setImportOpen(true); }}>
            <Icon name="plus" size={14} /> ייבוא לידים
          </button>
        </>}
      />

      <FilterRow>
        <SearchField value={q} onChange={setQ} placeholder="חיפוש לפי טלפון או שם" />
        {/* בורר הסטטוסים כמקטעים; הספירות מ-leadStats (לפי הקמפיין שנבחר). */}
        <Tabs
          items={STATUS_OPTS.map((o) => ({
            id: o.value || 'all',
            label: o.value ? o.label : 'הכל',
            count: stats ? (o.value ? (stats.by_status?.[o.value] ?? 0) : stats.total) : undefined,
          }))}
          value={status || 'all'}
          onChange={(id) => setStatus(id === 'all' ? '' : id)}
        />
      </FilterRow>

      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
          <span style={{ fontSize: 'var(--t-sm)' }}>{selected.size} נבחרו</span>
          <Select value="" onChange={(v) => v && bulk('assign', { agent_id: Number(v) })} options={[{ value: '', label: 'שייך לנציג…' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} />
          <Select value="" onChange={(v) => v && bulk('status', { status: v })} options={[{ value: '', label: 'שנה סטטוס…' }, ...STATUS_OPTS.filter((o) => o.value)]} />
          <button type="button" className="btn danger" onClick={() => bulk('delete')}>מחק</button>
          <button type="button" className="btn ghost" onClick={() => setSelected(new Set())}>נקה בחירה</button>
        </div>
      )}

      {/* כמו בעיצוב: כרטיס הטבלה נכנס 50ms אחרי שורות הכלים. */}
      <Card padded={false} style={{ '--d': '.05s' } as React.CSSProperties}>
        <DataTable<Lead>
          columns={cols}
          rows={rows}
          loading={loading}
          onRowClick={openDetail}
          emptyState={
            <EmptyState
              icon="users"
              title="אין נתונים"
              description="לא נמצאו לידים התואמים לחיפוש שלך."
              action={<button type="button" className="btn primary" onClick={() => { setImportCampaignId(''); setImportOpen(true); }}>+ ייבוא לידים</button>}
            />
          }
        />
      </Card>

      {importOpen && (
        <Modal isOpen onClose={() => setImportOpen(false)} title="ייבוא לידים" footer={
          <>
            <button type="button" className="btn" onClick={() => setImportOpen(false)}>ביטול</button>
            <button type="button" className="btn primary" onClick={doImport} disabled={importing}>{importing ? 'מייבא…' : 'ייבא'}</button>
          </>
        }>
          <div className="stack">
            {activeId == null && (
              <FormField label="קמפיין יעד" required hint="הלידים מיובאים לקמפיין (משם הם נכנסים גם למאגר).">
                <Select value={importCampaignId} onChange={setImportCampaignId} options={[{ value: '', label: '— בחר קמפיין —' }, ...campaigns.map((c) => ({ value: String(c.id), label: c.name }))]} />
              </FormField>
            )}
            <LeadImportInput onParsed={setParsedImport} />
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>כפילויות מול הקמפיין ידולגו אוטומטית.</div>
          </div>
        </Modal>
      )}

      <Drawer isOpen={!!detail} onClose={requestCloseDetail} title={detail ? `ליד · ${detail.phone}` : ''}>
        {detail && (
          <div className="stack" style={{ gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <FormField label="שם"><TextInput value={(editing.name as string) ?? ''} onChange={(ev) => setEditing((e) => ({ ...e, name: ev.target.value }))} /></FormField>
              <FormField label="סטטוס">
                <Select value={editing.status ?? ''} onChange={(v) => setEditing((e) => ({ ...e, status: v }))} options={STATUS_OPTS.filter((o) => o.value)} />
              </FormField>
              <FormField label="תוצאה (disposition)">
                <Select value={(editing.disposition as string) ?? ''} onChange={(v) => setEditing((e) => ({ ...e, disposition: v }))}
                  options={[{ value: '', label: '—' }, ...detail.dispositions.map((d) => ({ value: d.code, label: d.label }))]} />
              </FormField>
              <FormField label="נציג">
                <Select value={editing.agent_id ? String(editing.agent_id) : ''} onChange={(v) => setEditing((e) => ({ ...e, agent_id: v ? Number(v) : null }))}
                  options={[{ value: '', label: '— לא משויך' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} />
              </FormField>
              <FormField label="תזכורת חזרה (callback)">
                <input type="datetime-local" className="input" value={toLocalInput(editing.scheduled_callback_at as string)} onChange={(e) => setEditing((ed) => ({ ...ed, scheduled_callback_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
              </FormField>
              <FormField label="ניסיונות"><div style={{ padding: '8px 0', fontWeight: 600 }}>{detail.attempts}</div></FormField>
            </div>

            {/* שדות מותאמים — עריכים: שינוי/מחיקה/הוספה, נשמרים עם "שמור".
                זמינים גם ב-API ({{data.fields.<שם>}} באוטומציות/webhooks). */}
            <Card title="שדות מותאמים">
              <div className="stack" style={{ gap: 6 }}>
                {((editing._fields as [string, string][]) ?? []).map(([k, v], i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <TextInput
                      value={k}
                      placeholder="שם שדה (למשל: תקציב)"
                      aria-label={`שם שדה ${i + 1}`}
                      onChange={(ev) => setEditing((e) => ({ ...e, _fields: (e._fields as [string, string][]).map((p, j) => (j === i ? [ev.target.value, p[1]] : p)) }))}
                    />
                    <TextInput
                      value={v}
                      placeholder="ערך"
                      aria-label={`ערך שדה ${i + 1}`}
                      onChange={(ev) => setEditing((e) => ({ ...e, _fields: (e._fields as [string, string][]).map((p, j) => (j === i ? [p[0], ev.target.value] : p)) }))}
                    />
                    <button
                      type="button"
                      className="btn ghost"
                      aria-label="מחק שדה"
                      title="מחק שדה"
                      onClick={() => setEditing((e) => ({ ...e, _fields: (e._fields as [string, string][]).filter((_, j) => j !== i) }))}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setEditing((e) => ({ ...e, _fields: [...((e._fields as [string, string][]) ?? []), ['', '']] }))}
                >
                  + הוסף שדה
                </button>
              </div>
            </Card>

            <Card title={`היסטוריית שיחות (${detail.calls.length})`}>
              {detail.calls.length === 0 ? (
                <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין שיחות עדיין.</div>
              ) : detail.calls.map((c) => (
                <div key={c.id} className="ld-call-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 6px', borderBottom: '1px solid var(--line)', fontSize: 'var(--t-sm)' }}>
                  <span>{new Date(c.started_at).toLocaleString('he-IL')}</span>
                  <span style={{ color: 'var(--muted)' }}>{c.status}{c.talk_sec ? ` · ${fmtDuration(c.talk_sec)}` : ''}{c.disposition ? ` · ${c.disposition}` : ''}</span>
                </div>
              ))}
            </Card>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" className="btn danger" onClick={() => setConfirmState({ kind: 'deleteLead', id: detail.id })}>מחק ליד</button>
              <button type="button" className="btn primary" onClick={saveDetail}>שמור</button>
            </div>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        isOpen={!!confirmState}
        onClose={() => setConfirmState(null)}
        onConfirm={() => {
          if (!confirmState) return;
          if (confirmState.kind === 'discardChanges') {
            setDetail(null);
          } else if (confirmState.kind === 'bulkDelete') {
            void runBulk('delete');
          } else {
            void (async () => {
              try {
                await adminApi.deleteLead(confirmState.id);
                toast('הליד נמחק', 'ok');
                setDetail(null);
                loadLeads();
              } catch { toast('שגיאה במחיקה', 'danger'); }
            })();
          }
        }}
        title={
          confirmState?.kind === 'discardChanges' ? 'שינויים שלא נשמרו'
            : confirmState?.kind === 'bulkDelete' ? 'מחיקת לידים' : 'מחיקת ליד'
        }
        message={
          confirmState?.kind === 'discardChanges' ? 'השינויים בליד לא נשמרו. לצאת בלי לשמור?'
            : confirmState?.kind === 'bulkDelete' ? `למחוק ${confirmState.count} לידים?` : 'למחוק ליד זה?'
        }
        confirmLabel={confirmState?.kind === 'discardChanges' ? 'צא בלי לשמור' : 'מחק'}
        tone="danger"
      />
    </div>
  );
}

function statusTone(s: string) {
  return s === 'completed' ? 'ok' : s === 'calling' || s === 'connected' ? 'info' : s === 'failed' || s === 'dnc' ? 'danger' : 'neutral';
}
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
